type STTEvent =
    | { type: 'connected' }
    | { type: 'disconnected' }
    | { type: 'transcript'; text: string }
    | { type: 'error'; message: string };

class STTWebSocketManager {
    private ws: WebSocket | null = null;
    private listeners: Set<(event: STTEvent) => void> = new Set();
    private isConnecting = false;
    private shouldReconnect = false;
    private connectionParams: { baseURL: string; token: string; lang: string } | null = null;
    private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;

    connect(baseURL: string, token: string, lang: string) {
        if (this.ws?.readyState === WebSocket.OPEN || this.isConnecting) return;

        this.connectionParams = { baseURL, token, lang };
        this.shouldReconnect = true;
        this.isConnecting = true;

        const wsURL = baseURL.replace(/^http/, 'ws') +
            `/api/v1/stt/stream?lang=${lang}${token ? `&token=${token}` : ''}`;

        this.ws = new WebSocket(wsURL);
        this.ws.binaryType = 'arraybuffer';

        this.ws.onopen = () => {
            this.isConnecting = false;
            if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); this.reconnectTimeout = null; }
            this.notify({ type: 'connected' });
        };

        this.ws.onmessage = (event) => {
            try {
                const msg = JSON.parse(event.data as string);
                if (msg.error) {
                    this.notify({ type: 'error', message: msg.error });
                } else if (msg.done && msg.text !== undefined) {
                    this.notify({ type: 'transcript', text: msg.text });
                }
            } catch {
                // ignore unparseable frames
            }
        };

        this.ws.onerror = () => {
            this.isConnecting = false;
            this.notify({ type: 'error', message: 'WebSocket error' });
        };

        this.ws.onclose = () => {
            this.isConnecting = false;
            this.ws = null;
            this.notify({ type: 'disconnected' });
            if (this.shouldReconnect && this.connectionParams) {
                this.reconnectTimeout = setTimeout(() => {
                    this.connect(this.connectionParams!.baseURL, this.connectionParams!.token, this.connectionParams!.lang);
                }, 3000);
            }
        };
    }

    /** Send a complete WAV ArrayBuffer to the server for transcription. */
    sendAudio(wavBuffer: ArrayBuffer) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(wavBuffer);
        }
    }

    addListener(cb: (event: STTEvent) => void) { this.listeners.add(cb); }
    removeListener(cb: (event: STTEvent) => void) { this.listeners.delete(cb); }
    private notify(event: STTEvent) { this.listeners.forEach(l => l(event)); }

    isConnected() { return this.ws?.readyState === WebSocket.OPEN; }

    disconnect() {
        this.shouldReconnect = false;
        if (this.reconnectTimeout) { clearTimeout(this.reconnectTimeout); this.reconnectTimeout = null; }
        this.ws?.close();
        this.ws = null;
        this.connectionParams = null;
    }
}

export const sttWebSocket = new STTWebSocketManager();

// ---------------------------------------------------------------------------
// WAV encoding helpers
// ---------------------------------------------------------------------------

function writeString(view: DataView, offset: number, str: string) {
    for (let i = 0; i < str.length; i++) {
        view.setUint8(offset + i, str.charCodeAt(i));
    }
}

export function encodeWAV(pcmBuffers: Int16Array[], sampleRate: number): ArrayBuffer {
    const totalSamples = pcmBuffers.reduce((s, b) => s + b.length, 0);
    const byteCount = totalSamples * 2;
    const buffer = new ArrayBuffer(44 + byteCount);
    const view = new DataView(buffer);

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + byteCount, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);   // PCM
    view.setUint16(22, 1, true);   // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(view, 36, 'data');
    view.setUint32(40, byteCount, true);

    let offset = 44;
    for (const buf of pcmBuffers) {
        for (let i = 0; i < buf.length; i++) {
            view.setInt16(offset, buf[i], true);
            offset += 2;
        }
    }
    return buffer;
}
